import React, { useEffect, useState, useRef } from 'react';
import { ICertificateLocal, TranslationsTypes } from '@app/models';
import { Card, CardContent, CardActions, Box, makeStyles, Theme, createStyles } from '@material-ui/core';
import { PasslyButton } from '@app/components/Buttons';

import { useTypedTranslation } from '@app/hooks';
import { Code, GetApp } from '@material-ui/icons';
import { passlyFormatDate, DATE_FORMAT } from '@app/helpers';
import { PasslyTextField } from '@app/components';

interface IProps {
    localCertificate: ICertificateLocal | null;
    cardAditionalAction: any;
    certType?: string;
    showKeys?: any[];
}

interface CertificateRow { type: string; label: TranslationsTypes; value: string | undefined; }

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            marginBottom: theme.spacing(2)
        },
        heading: {
            paddingTop: 0,
        },
        cardActions: {
            padding: theme.spacing(2),
            '& [class^="MuiButton"]': {
                marginRight: theme.spacing(1),
            },
            '& > *': {
                '&:last-child': {
                    marginLeft: 'auto',
                    marginRight: 0
                }
            }
        }
    })
);

export const Certificate: React.SFC<IProps> = ({localCertificate, cardAditionalAction, certType, showKeys= []}) => {
    const classes = useStyles();
    const { translation } = useTypedTranslation();

    const [state, setState] = useState(false);
    const textAreaRef = useRef<HTMLInputElement>();

    const selectTextCert = () => {
        textAreaRef?.current?.focus();
        textAreaRef?.current?.select();
        textAreaRef?.current?.setSelectionRange(0, 99999);
    };

    useEffect(() => {
        selectTextCert();
    }, [state]);


    const toggleEditCert = () => {
        setState(!state);
        textAreaRef.current?.focus();
    };


    const rows: CertificateRow[] = [
        {
            type: 'Subject',
            label: 'CERTIFICATE_SUBJECT',
            value: localCertificate?.certificate?.Subject,
        },
        {
            type: 'Issuer',
            label: 'CERTIFICATE_ISSUER',
            value: localCertificate?.certificate?.Issuer,
        },
        {
            type: 'Date',
            label: 'CERTIFICATE_EXPIRY',
            value: `${passlyFormatDate(localCertificate?.certificate?.NotBefore as string, DATE_FORMAT)} - ${passlyFormatDate(localCertificate?.certificate?.NotAfter as string, DATE_FORMAT)}`,
        },
        {
            type: 'Thumbprint',
            label: 'CERTIFICATE_THUMBPRINT',
            value: localCertificate?.certificate?.Thumbprint,
        }
    ];

    const filterRows = (): CertificateRow[] => {
        return showKeys?.length ? rows.filter((item: any) => showKeys.includes(item.type)) : rows;
    };

    return (
        <>
            <Card className={classes.root}>
                <CardContent>
                    <h3 className={classes.heading}>{certType}</h3>
                    <Box>
                        <dl className='description-list'>
                            {filterRows().map(({label, value}, index: number) => (
                                <React.Fragment key={index}>
                                    <dt>{translation(label)}</dt>
                                    <dd>{value}</dd>
                                </React.Fragment>
                            ))}
                        </dl>
                    </Box>
                    {state &&
                        <Box>
                            <h3>{localCertificate?.certificate?.Subject}</h3>
                            <PasslyTextField
                                id='certificate-preview-input'
                                inputRef={textAreaRef}
                                multiline
                                value={localCertificate?.certificate?.CerEncoded}
                                onClick={selectTextCert}
                                className='certificate-preview'
                            />
                        </Box>
                    }
                </CardContent>
                <CardActions className={classes.cardActions}>
                    <PasslyButton
                        id='editCertificate'
                        variant='contained'
                        color='primary'
                        onClick={() => toggleEditCert()}
                        startIcon={<Code />}
                    >
                        {translation('SHOW_APP_SIGNING_CERT')}
                    </PasslyButton>
                    <PasslyButton
                        id='downloadCertificate'
                        variant='contained'
                        color='primary'
                        href={localCertificate?.certUrl}
                        download={localCertificate?.certFileName}
                        startIcon={<GetApp />}
                    >
                        {translation('DOWNLOAD_APP_SIGNING_CERT')}
                    </PasslyButton>
                    {cardAditionalAction}
                </CardActions>
            </Card>
        </>
    );
};
